import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminHeaders, validateAdminSession } from '../../utils/adminAuth';
import './ResetLiabilityWaiversPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ResetLiabilityWaiversPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [doneCount, setDoneCount] = useState<number | null>(null);

  useEffect(() => {
    const check = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (session.role !== 'breakglass') {
        navigate('/admin-panel');
        return;
      }
      setLoading(false);
    };
    check();
  }, [navigate]);

  const resetAll = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/liability-waivers/reset-all`, {
        method: 'POST',
        headers: getAdminHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to reset liability waivers');
        return;
      }
      setDoneCount(typeof data.usersReset === 'number' ? data.usersReset : 0);
      setConfirming(false);
    } catch {
      setError('Failed to reset liability waivers');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="reset-waivers-container">
        <div className="reset-waivers-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-waivers-container">
      <div className="reset-waivers-card">
        <h1>Reset all liability waivers</h1>
        <p className="reset-waivers-copy">
          This clears the liability waiver for every volunteer in the system, including dependents.
          Each waiver becomes invalid, so that person has to accept it again. No emails are sent.
        </p>
        {doneCount !== null && (
          <p className="reset-waivers-done">
            Reset {doneCount} {doneCount === 1 ? 'person' : 'people'}. Their liability waivers are now invalid.
          </p>
        )}
        {error && <p className="reset-waivers-error">{error}</p>}
        <button type="button" className="reset-waivers-start" onClick={() => { setError(''); setConfirming(true); }}>
          Reset all liability waivers
        </button>
        <button type="button" className="reset-waivers-back" onClick={() => navigate('/admin-panel')}>
          Back to Admin Panel
        </button>
      </div>

      {confirming && (
        <div className="reset-warning-overlay" onClick={() => { if (!saving) setConfirming(false); }}>
          <div className="reset-warning-box" onClick={(e) => e.stopPropagation()}>
            <h2>This will reset all liability waivers in the whole system.</h2>
            <p>No emails will be sent. Everyone will have to accept the waiver again.</p>
            {error && <p className="reset-waivers-error">{error}</p>}
            <div className="reset-warning-actions">
              <button type="button" onClick={() => setConfirming(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="reset-warning-confirm" onClick={resetAll} disabled={saving}>
                {saving ? 'Resetting...' : 'Reset all liability waivers'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetLiabilityWaiversPage;
